import { IProduct, IUnavailablePlacement } from '../../shared/models/product.model';
import { IRectangle, Rectangle } from '../../widget-grid/models/Rectangle.model';
import { IDeviceConfiguration } from './device-data.model';
import { IApplicationGroups } from '../../shared/models/device.model';
import { ElementRef } from '@angular/core';
import { ISLDConnection } from './sld.model';

export interface IEnclosureSwitchboardGrid {
  devicesGrid: IGrid;
  combGrid: IGrid;
  powerTagGrid: IGrid;
}

export interface IGrid {
  rows: IRow[];
  height: number;
  width: number;
  enclosureField?: IGridPositionIndex;
  reference?: string;
  canSelectDevicesBetweenRows?: boolean;
  rotated?: boolean;
}

export interface IRow {
  rowNumber: number;
  placePosition: IRowPlacedDimensions;
  fields: IField[];
  onlyAllowedDevicesRangesIDs?: number[];
  specialFieldPlacementRules?: IUnavailablePlacement[];
  adjustedGridToDevices?: IAdjustableGrids[];
  rowHasCover?: boolean;
  removeForProductRangeIDs?: number[];
  allowsPatchpanels: boolean;
  pragmaMultiCustomizedPowerTagNotAllowed: boolean;
}

export interface IAdjustableGrids {
  deviceReference: string;
  gridAdjustedSize: IGridDeviceAdjustedSize;
}

export interface IPosition {
  top: number;
  left: number;
}

export interface IPositionHighlight extends IPosition{
  allowPlacement: boolean;
}

export interface IRowPlacedDimensions extends IPosition {
  width: number;
  coverLabelPositionY?: number;
}

export interface IGridPlacePosition extends IPosition {
  top: number;
  left: number;
  gridIndex: IGridPositionIndex;
}

export interface IDevicesPlacePosition extends IGridPlacePosition{
  devicesUUID: string;
}

export interface IField {
  position: IPosition;
  highlightColor?: string;
  fieldPinStatus: PinStatus;
}

export interface IHighlightedField {
  position: IPosition;
  placingAllowed: boolean;
}

export enum PinStatus {
  free = 0,
  neutral = 1,
  phase = 2,
  gap = 3,
  no_pin = 4,
  half_phase_left = 5,
  half_phase_right = 6,
  half_neutral_left = 7,
  half_neutral_right = 8,
  universal = 9,
  half_single_phase_left = 10,
  half_single_phase_right = 11,
  half_single_neutral_left = 12,
  half_single_neutral_right = 13,
}

export interface IPlacedDevice {
  uuid: string;
  id?: number;
  position: IPosition;
  width: number;
  enclosureField?: IGridPositionIndex;
  options?: string[];
  device: IProduct;
  sldConnection: ISLDConnection;
}

export interface IMovingDevice extends IPlacedDevice {
  availablePositions: IAvailablePositions[];
}

export interface IAvailablePositions {
  gridIndex: IGridPositionIndex;
  positions: IPosition[];
}

export interface IResizeWidget {
  placedDevice: IPlacedDevice;
  newSize: number;
}

export interface IArea {
  position: IPosition;
  width: number;
}

export enum DeviceOnGridType {
  combBar = 0,
  device = 1,
  powerTag = 2
}

export interface IContainerDimensions {
  containerHeight: number;
  containerWidth: number;
}

export interface IMousePosition {
  x: number;
  y: number;
}

export interface IDeviceGridPattern {
  pattern: PinStatus[];
  repeatingPosition: number;
}

export interface IHighlightArea {
  area: IRectangle;
  allowPlace: boolean;
}

export interface IHighlightConfigurationArea {
  rectangle: Rectangle;
  color: string;
}

export interface ISideMenuDeviceDetails {
  propertyName: string;
  device: IPlacedDevice;
  deviceConfig?: IDeviceConfiguration;
  label?: string;
  applicationGroup?: IApplicationGroups;
  description?: string;
}

export interface ISideMenuLabelsDetails {
  propertyName: string;
  device: IPlacedDevice;
  value: string | IApplicationGroups;
}

export interface ISideMenuCustomDeviceDetails {
  event: any;
  fieldName: string;
  device: IPlacedDevice;
}

export interface ISideMenuInputUpdate {
  event: any;
  fieldName: string;
  device: IPlacedDevice;
}

export interface IGridPositionIndex {
  top: number;
  left: number;
}

export interface IGridDetails {
  enclosureImageDimensions: IContainerDimensions;
  firstRowSlotsWidth: number;
  firstRowDimensionsWidth: number;
  heightWidthRatio: number;
  onlySupportedRangesOnGrid: number[];
  enclosureRows?: number;
  specialKitsOnGridReferences?: string[];
}

export interface IMoveReferentDevice {
  position: IGridPlacePosition;
  devicesRectangle: IRectangle;
}

export interface IDeviceInSegment {
  position: IPosition;
  deviceUUID: string;
}

export interface IMoveSegment {
  referentDevice: IMoveReferentDevice;
  movingDevices: IDeviceInSegment[];
}

export interface IAccessoryOnDevice {
  selectedDeviceID: number;
  accessoryOn: boolean;
}

export interface ISelectedWidgetsOnGrid {
  widget: IPlacedDevice;
  dimensions: IRectangle;
  attachedPowerTag?: IAttachedPowerTagToSelectedWidget;
}

export interface ISegment {
  position: IRectangle;
  dimensions: IRectangle;
  selectedWidgets: ISelectedWidgetsOnGrid[];
  referentDevicePosition: IGridPlacePosition;
}

export interface IRepeatingBackgrounds {
  backgrounds: string;
  backgroundSize: string;
}

export interface IToggleSelectList {
  mainContainerRect: ElementRef;
  scrollTopDifference: number;
}

export interface IRailPlaceholder {
  showRail: boolean;
  railWidth?: number;
  railsHeight?: number;
  offsetX?: number;
}

export interface IGridDeviceAdjustedSize {
  railWidth: number;
  railsHeight: number;
  offsetX: number;
  deviceWidth: number;
}

export interface ISpecialKitOffset {
  offsetX: number;
  offsetY: number;
}

export interface IRemovedDeviceFromSwitchboard {
  device: IPlacedDevice;
  index: number;
}

export interface ISwitchboardGrid {
  grid: IGrid[];
  gridType: DeviceOnGridType;
}

export interface IConfirmMoveDevicesFromModal {
  previousDevices: IMovingDevice[];
  devicesWithNewPositions: IPlacedDevice[];
}

export interface IConfirmMoveSingleDeviceFromModal {
  previousDevice: IMovingDevice;
  deviceWithNewPosition: IPlacedDevice;
}

export interface IAttachedPowerTag {
  powerTagDevice: IPlacedDevice;
  positionOffset: number;
  attachedToDeviceUuid: string;
}

export interface IAttachedPowerTagToSelectedWidget {
  widget: IPlacedDevice;
  dimensions: IRectangle;
}
